import React, { useState, useEffect } from 'react';
import {
  CreateDevForm,
  RenderDevProj,
  EditDevForm,
  ProjectSelector,
  PreviewDevContent,
} from '../components';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteAllDevProjects,
  getDevProjectBody,
  getDevProjects,
} from '../features/projects/devprojectSlice';
import {
  Button,
  Modal,
  Typography,
  Space,
  Tabs,
  Card,
  Divider,
  message,
  Spin,
} from 'antd';
import {
  CodeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { confirm } = Modal;

const Development = () => {
  const [activeTab, setActiveTab] = useState('create');
  const [bodyContent, setBodyContent] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const dispatch = useDispatch();
  const { devprojects, loading, bodyLoading, bodyError } = useSelector(
    (state) => state.devprojects
  );

  const handlePurgeProjects = () => {
    confirm({
      title: 'Delete All Development Projects',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            Are you sure you want to delete{' '}
            <strong>all {devprojects.length} development projects</strong>?
          </p>
          <p className='text-red-600 font-medium'>
            This action cannot be undone and will permanently remove:
          </p>
          <ul className='mt-2 ml-4 text-red-600'>
            <li>• All project data and descriptions</li>
            <li>• All uploaded project images</li>
            <li>• All project links and metadata</li>
            <li>• All project content and documentation</li>
          </ul>
        </div>
      ),
      okText: 'Yes, Delete All',
      okType: 'danger',
      cancelText: 'Cancel',
      width: 500,
      onOk() {
        dispatch(deleteAllDevProjects());
      },
    });
  };

  const [refreshing, setRefreshing] = useState(false);

  // Replace your handleRefresh function with this enhanced version
  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      // Refresh the development projects data
      await dispatch(getDevProjects()).unwrap();

      // If there's a selected project, refresh its body content as well
      if (selectedProject) {
        try {
          const result = await dispatch(
            getDevProjectBody(selectedProject._id)
          ).unwrap();
          setBodyContent(result.body || '');
        } catch (error) {
          console.log('Error refreshing project body:', error);
        }
      }
    } catch (error) {
      // Show error message if refresh fails
      message.error('Failed to refresh projects');
      console.error('Refresh error:', error);
    } finally {
      // Stop the spinning animation
      setRefreshing(false);
    }
  };

  const handleAddDetailedContent = () => {
    if (devprojects.length === 0) {
      Modal.info({
        title: 'No Projects Available',
        content:
          'Please create a development project first before adding detailed content.',
        onOk: () => setActiveTab('create'),
      });
      return;
    }
    setActiveTab('editor');
  };

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);

    // If the project already has body content, use it
    if (project.body && project.body.trim()) {
      setBodyContent(project.body);
    } else {
      // Try to fetch the body content from the server
      try {
        const result = await dispatch(getDevProjectBody(project._id)).unwrap();
        setBodyContent(result.body || '');
      } catch (error) {
        console.log('No body content found or error fetching:', error);
        setBodyContent('');
      }
    }

    setActiveTab('editor');
  };

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;

    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace

    return textContent === '';
  };

  const handleSaveContent = () => {
    setActiveTab('projects');
    setSelectedProject(null);
    setBodyContent('');
  };

  const handleCancelEdit = () => {
    setActiveTab('projects');
    setSelectedProject(null);
    setBodyContent('');
  };

  // Count projects with content for badge
  const projectsWithContent = devprojects.filter(
    (project) =>
      project.body && project.body.trim() && !isContentEmpty(project.body)
  ).length;

  return (
    <div className='max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen'>
      {/* Header */}
      <div className='mb-8'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-3'>
            <CodeOutlined className='text-2xl text-blue-600' />
            <div>
              <Title level={2} className='m-0'>
                Development Projects
              </Title>
              <Text className='text-gray-600'>
                Create and manage your development portfolio projects
              </Text>
            </div>
          </div>
          <Space>
            <Button
              icon={<EditOutlined />}
              onClick={handleAddDetailedContent}
              type='primary'
              ghost
            >
              Add Detailed Content
            </Button>
            {devprojects.length > 0 && (
              <Button
                icon={<DeleteOutlined />}
                onClick={handlePurgeProjects}
                loading={loading}
                danger
                type='primary'
              >
                Purge All ({devprojects.length})
              </Button>
            )}
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={handleRefresh}
              loading={refreshing}
              disabled={refreshing}
            >
              Refresh
            </Button>
          </Space>
        </div>
      </div>

      {/* Main Content */}
      <Card className='shadow-soft border border-gray-200 rounded-xl bg-white'>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size='large'
          className='min-h-[600px]'
        >
          <TabPane
            tab={
              <Space>
                <CodeOutlined />
                Create Project
              </Space>
            }
            key='create'
          >
            <div className='p-4'>
              <CreateDevForm />
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                📋 My Projects
                {devprojects.length > 0 && (
                  <span className='bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium'>
                    {devprojects.length}
                  </span>
                )}
              </Space>
            }
            key='projects'
          >
            <div className='p-4'>
              {devprojects.length > 0 ? (
                <>
                  <div className='mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg'>
                    <div className='flex items-center justify-between'>
                      <div>
                        <Text className='text-blue-800 font-medium block'>
                          📊 Portfolio Summary
                        </Text>
                        <Text className='text-blue-600 text-sm'>
                          You have {devprojects.length} development project
                          {devprojects.length !== 1 ? 's' : ''} in your
                          portfolio
                        </Text>
                      </div>
                      <div className='text-right'>
                        <div className='text-2xl font-bold text-blue-800'>
                          {devprojects.length}
                        </div>
                        <div className='text-xs text-blue-600 uppercase tracking-wide'>
                          Projects
                        </div>
                      </div>
                    </div>
                  </div>

                  <Divider
                    orientation='left'
                    className='text-gray-600 font-medium'
                  >
                    Your Development Projects
                  </Divider>

                  <RenderDevProj onEditContent={handleProjectSelect} />
                </>
              ) : (
                <div className='text-center py-16'>
                  <CodeOutlined className='text-6xl text-gray-400 mb-4' />
                  <Title level={3} className='text-gray-500 mb-2'>
                    No Development Projects Yet
                  </Title>
                  <Text className='text-gray-400 text-base mb-6 block'>
                    Start building your portfolio by creating your first
                    development project
                  </Text>
                  <Button
                    type='primary'
                    size='large'
                    onClick={() => setActiveTab('create')}
                    className='bg-blue-600 hover:bg-blue-700'
                  >
                    Create Your First Project
                  </Button>
                </div>
              )}
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                <EyeOutlined />
                Preview Content
                {projectsWithContent > 0 && (
                  <span className='bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium'>
                    {projectsWithContent}
                  </span>
                )}
              </Space>
            }
            key='preview'
            disabled={projectsWithContent === 0}
          >
            <div className='p-4'>
              <PreviewDevContent onEditContent={handleProjectSelect} />
            </div>
          </TabPane>

          <TabPane
            tab={
              <Space>
                <EditOutlined />
                {selectedProject
                  ? `${selectedProject.name} Content`
                  : 'Select Project'}
              </Space>
            }
            key='editor'
            disabled={devprojects.length === 0}
          >
            <div className='p-6'>
              {selectedProject ? (
                <EditDevForm
                  selectedProject={selectedProject}
                  onCancel={handleCancelEdit}
                  onSave={handleSaveContent}
                  bodyContent={bodyContent}
                  setBodyContent={setBodyContent}
                />
              ) : (
                <>
                  {devprojects.length > 0 ? (
                    <ProjectSelector
                      projects={devprojects}
                      onProjectSelect={handleProjectSelect}
                      type='dev'
                      isContentEmpty={isContentEmpty}
                    />
                  ) : (
                    <Card className='mb-6'>
                      <div className='text-center py-12'>
                        <CodeOutlined className='text-6xl text-gray-400 mb-4' />
                        <Title level={4} className='text-gray-500 mb-2'>
                          No Projects Available
                        </Title>
                        <Text className='text-gray-400 mb-6 block'>
                          Create your first development project to add detailed
                          content
                        </Text>
                        <Button
                          type='primary'
                          size='large'
                          onClick={() => setActiveTab('create')}
                          className='bg-blue-600 hover:bg-blue-700'
                        >
                          Create Your First Project
                        </Button>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* Quick Stats Footer */}
      {devprojects.length > 0 && (
        <div className='mt-6 grid grid-cols-1 md:grid-cols-4 gap-4'>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-green-600'>
              {devprojects.filter((p) => p.status === true).length}
            </div>
            <div className='text-sm text-gray-600'>Completed</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-orange-600'>
              {devprojects.filter((p) => p.status === false).length}
            </div>
            <div className='text-sm text-gray-600'>In Progress</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-blue-600'>
              {new Set(devprojects.flatMap((p) => p.stacks || [])).size}
            </div>
            <div className='text-sm text-gray-600'>Technologies Used</div>
          </Card>
          <Card size='small' className='text-center'>
            <div className='text-2xl font-bold text-purple-600'>
              {projectsWithContent}
            </div>
            <div className='text-sm text-gray-600'>With Documentation</div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Development;
